import { ToastManager } from '../ui/components/Toast';

import { ApiClient } from './ApiClient';
import { AuthStore } from '../core/store/authStore';
import { supabase } from './supabase';
import { EventBus } from '../core/events/EventBus';

export interface Friend {
  id: string;
  username: string;
  tag?: string;
  avatarUrl?: string;
  avatar_id?: string;
  status: 'online' | 'in_game' | 'offline';
  bestGame?: string;
  topScore: number;
}

export class FriendService {
  private static presenceChannel: ReturnType<typeof supabase.channel> | null = null;
  private static onlineUsers: Map<string, { status: string; gameId?: string }> = new Map();

  static async getFriends(): Promise<Friend[]> {
    const user = AuthStore.getUser();
    if (!user) return [];

    const res = await ApiClient.getFriends(user.id);
    if (res.error || !res.data) return [];

    return res.data.map(p => {
      const presence = this.onlineUsers.get(p.id);
      return {
        id: p.id,
        username: p.username,
        tag: p.player_tag,
        avatar_id: p.avatar_id,
        status: presence ? (presence.status as 'online' | 'in_game') : 'offline',
        bestGame: presence?.gameId,
        topScore: 0 // Fetch score separately if needed
      };
    });
  }

  static async getRequests(): Promise<any[]> {
    const user = AuthStore.getUser();
    if (!user) return [];
    const res = await ApiClient.getFriendRequests(user.id);
    return res.data || [];
  }

  static async acceptRequest(friendId: string): Promise<boolean> {
    const user = AuthStore.getUser();
    if (!user) return false;
    const res = await ApiClient.acceptFriendRequest(user.id, friendId);
    if (!res.error) {
      EventBus.emit('friends:updated', {});
      return true;
    }
    return false;
  }

  static async rejectRequest(friendId: string): Promise<boolean> {
    const user = AuthStore.getUser();
    if (!user) return false;
    const res = await ApiClient.rejectFriendRequest(user.id, friendId);
    if (!res.error) {
      EventBus.emit('friends:updated', {});
      return true;
    }
    return false;
  }

  static async addFriend(usernameOrTag: string): Promise<boolean> {
    const user = AuthStore.getUser();
    if (!user) {
      ToastManager.show('Arkadaş eklemek için giriş yapmalısınız.', 'warning');
      return false;
    }

    if (!usernameOrTag.includes('#')) {
      ToastManager.show('Lütfen oyuncu etiketini tam girin (örn: Player#1234)', 'warning');
      return false;
    }

    const tag = usernameOrTag.substring(usernameOrTag.indexOf('#'));
    const resUser = await ApiClient.searchByTag(tag);

    if (resUser.error || !resUser.data) {
      ToastManager.show('Oyuncu bulunamadı.', 'error');
      return false;
    }

    if (resUser.data.id === user.id) {
      ToastManager.show('Kendinizi ekleyemezsiniz.', 'warning');
      return false;
    }

    const res = await ApiClient.addFriend(user.id, resUser.data.id);
    if (res.error) {
      ToastManager.show('Arkadaşlık isteği gönderilemedi.', 'error');
      return false;
    }

    ToastManager.show(`📬 ${resUser.data.username} kullanıcısına arkadaşlık isteği gönderildi!`, 'success');
    return true;
  }

  static async removeFriend(friendId: string): Promise<void> {
    const user = AuthStore.getUser();
    if (!user) return;
    
    await ApiClient.removeFriend(user.id, friendId);
    ToastManager.show('Arkadaş silindi.', 'info');
  }

  // ─── PRESENCE & REALTIME ────────────────────────────────────────────────

  static initPresence() {
    const user = AuthStore.getUser();
    if (!user) return;

    if (this.presenceChannel) return;

    this.presenceChannel = supabase.channel('online-users', {
      config: { presence: { key: user.id } }
    });

    this.presenceChannel
      .on('presence', { event: 'sync' }, () => {
        const state = this.presenceChannel!.presenceState();
        this.onlineUsers.clear();
        for (const [userId, presences] of Object.entries(state)) {
          if (presences.length > 0) {
            const p = presences[0] as any;
            this.onlineUsers.set(userId, { status: p.status, gameId: p.gameId });
          }
        }
        EventBus.emit('friends:updated', {});
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await this.updatePresence('online', 'lobby');
        }
      });
  }

  static async updatePresence(status: 'online' | 'in_game', gameId?: string) {
    if (!this.presenceChannel) return;
    await this.presenceChannel.track({ status, gameId, updatedAt: new Date().toISOString() });
  }

  static disconnectPresence() {
    if (this.presenceChannel) {
      this.presenceChannel.unsubscribe();
      this.presenceChannel = null;
    }
    this.onlineUsers.clear();
  }
}
